/**
 * How the Images tab groups what the user photographed.
 *
 * The legacy contract tags every upload with a category — `car_image` or
 * `part_image` — and the tab shows three cards built from that tag: Exteriors,
 * Close-ups, Identifiers. Hegic's `carImages` carry no such tag; `name` is the
 * file's own name ("image1.jpeg"). Every Hegic upload therefore fails both
 * checks and lands in Identifiers, which is not a classification so much as a
 * mislabel — the tab claims a demarcation the data never made.
 *
 * So the grouping is decided by the data, not by a per-client flag: a flag
 * would live in the portal-config record, and records seeded before this change
 * would not carry it, leaving the mislabel in place for exactly the sessions
 * that hit it. The category token is either present or it is not.
 */

// GET /uploads adds three more, all identifiers: a case with only an odometer
// shot is still categorised, so the tab groups rather than dumping it flat.
const CATEGORY_TOKENS = new Set([
  "car_image",
  "part_image",
  "engine_image",
  "chasis_image",
  "odo_meter_image",
]);

/** True when at least one upload declares which bucket it belongs in. */
export const uploadsAreCategorised = (uploads = []) =>
  uploads.some((upload) => CATEGORY_TOKENS.has(upload?.name));

/** Sides arrive as `front_right`; a caption wants FRONT RIGHT. */
export const prettySide = (side) =>
  typeof side === "string" ? side.replace(/[_-]+/g, " ").trim().toUpperCase() : "";

/**
 * Some uploads carry no separate `side` field at all — the side is baked into
 * the file name instead, as a prefix before "-CODE_<id>" (e.g.
 * "FRONT-LEFT-CODE_382454984"). Current design's Exteriors/Close-ups cards
 * already parse this out; the redesign needs the same fallback or every such
 * upload captions itself "Image N" (or, worse, a plain filename like
 * "image5.jpeg" blindly run through the same stripping would come out as the
 * equally wrong "IMAGEJPEG" — so this only extracts when the name actually
 * carries the "-CODE_<id>" marker, "" otherwise, leaving a plain filename for
 * the next fallback in the chain to handle unchanged.
 */
const CODE_SUFFIX = /-CODE_\d+$/i;
export const extractSideFromName = (name) => {
  if (typeof name !== "string" || !CODE_SUFFIX.test(name)) return "";
  return name
    .replace(CODE_SUFFIX, "")
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/[^a-z_]/g, "");
};

/**
 * The most specific true thing about one upload. A category token names the
 * kind of shot; without one the side is the only real label the API gives, and
 * failing that the file name — never an invented category.
 */
const TITLES = {
  car_image: "Exterior",
  part_image: "Close-up",
  engine_image: "Engine",
  chasis_image: "Chassis",
  odo_meter_image: "Odometer",
};

export const uploadTitle = (upload = {}, index = 0) => {
  if (TITLES[upload.name]) return TITLES[upload.name];
  return (
    prettySide(upload.side) ||
    prettySide(extractSideFromName(upload.name)) ||
    upload.name ||
    `Image ${index + 1}`
  );
};

/**
 * Every upload, in the order the API returned them, with no bucketing.
 * `mediaUrl` is passed in rather than imported so this stays free of client
 * and session state.
 */
export const toCapturedImages = (uploads = [], mediaUrl = (p) => p) =>
  uploads.map((upload, index) => ({
    id: upload.id ?? `upload-${index}`,
    name: upload.name,
    imageUrl: mediaUrl(upload.upload_picture || upload.picture),
    fileName: upload.file_name || upload.name || "Unknown",
    side: prettySide(upload.side),
    title: uploadTitle(upload, index),
  }));

export default toCapturedImages;
